import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';

import { Typography } from 'src/typography';
import { CheckIcon as _CheckIcon } from 'src/assets/check';
import LoginForm from 'components/forms/login-form';
import SignUpForm from 'components/forms/sign-up-form';
import { GoogleLogin, AppleLogin } from 'src/components/social-login';
import { Divider } from 'src/components/divider';
import { INSTORE_ENROLLMENT_SOURCE } from 'src/components/instore-enrollment';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { Button as _Button } from 'src/components/core';

type ViewState = 'email-login' | 'login' | 'signup';

type LoginViewProps = {
  displayEmailLogin: boolean;
  onToggleEmailLogin: () => void;
};

type SignupViewProps = {
  isVisible: boolean;
};

type FooterProps = {
  isSignup: boolean;
  onToggle: () => void;
};

const CHECKLIST_ITEMS = ['A free phone consultation', 'A free medical certification'] as const;

const LoginView = ({ displayEmailLogin, onToggleEmailLogin }: LoginViewProps): JSX.Element => (
  <ContentContainer data-testid='login-content-container'>
    <HeadingContainer>
      <Typography.Body size='medium'>Get started with:</Typography.Body>
      {CHECKLIST_ITEMS.map((item) => (
        <ChecklistItem key={item}>
          <CheckIcon /> {item}
        </ChecklistItem>
      ))}
    </HeadingContainer>

    <Logins>
      <AnimatedContentContainer $isVisible={!displayEmailLogin}>
        <LoginText>Log in or sign up to get started</LoginText>
        <GoogleLogin redirectUrl={window.location.href} />
        <AppleLogin redirectUrl={window.location.href} />
        <Button variant='secondary' onClick={onToggleEmailLogin}>
          Continue with email
        </Button>
      </AnimatedContentContainer>

      <AnimatedContentContainer $isVisible={displayEmailLogin}>
        <LoginText>Log in to your account</LoginText>
        <LoginForm onBack={onToggleEmailLogin} analyticsEventLabel={INSTORE_ENROLLMENT_SOURCE} />
      </AnimatedContentContainer>
    </Logins>
  </ContentContainer>
);

const SignupView = ({ isVisible }: SignupViewProps): JSX.Element => (
  <AnimatedContentContainer data-testid='signup-content-container' $isVisible={isVisible}>
    <ContentContainer>
      <HeadingContainer>
        <Typography.Heading size='medium'>Create an account</Typography.Heading>
      </HeadingContainer>

      <Logins>
        <GoogleLogin redirectUrl={window.location.href} />
        <AppleLogin redirectUrl={window.location.href} />
      </Logins>

      <Divider text='or enter your information' />

      <SignUpForm analyticsEventLabel={INSTORE_ENROLLMENT_SOURCE} displayLoginLink={false} />
    </ContentContainer>
  </AnimatedContentContainer>
);

const Footer = ({ isSignup, onToggle }: FooterProps): JSX.Element => (
  <FooterContainer>
    <BodyText size='small'>{isSignup ? 'Already have an account?' : "Don't have an account?"}</BodyText>
    <SignupLink data-testid={isSignup ? 'login-link' : 'signup-link'} size='small' onClick={onToggle}>
      {isSignup ? 'Log In' : 'Sign Up'}
    </SignupLink>
  </FooterContainer>
);

export function Login(): JSX.Element {
  const [viewState, setViewState] = useState<ViewState>('login');
  const { dispensary } = useDispensary();

  const showSignup = (): void => setViewState('signup');
  const showLogin = (): void => setViewState('login');
  const toggleEmailLogin = (): void => setViewState(viewState === 'email-login' ? 'login' : 'email-login');

  // Reset scroll position when toggling between login and signup views
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [viewState]);

  const { logoImage, name } = dispensary;

  return (
    <>
      <MainContainer data-testid='login-main-container'>
        <DispensaryLogoImage src={logoImage} alt={`${String(name)} Logo`} />
        {viewState !== 'signup' && (
          <LoginView displayEmailLogin={viewState === 'email-login'} onToggleEmailLogin={toggleEmailLogin} />
        )}
        <SignupView isVisible={viewState === 'signup'} />
        <Footer isSignup={viewState === 'signup'} onToggle={viewState === 'signup' ? showLogin : showSignup} />
      </MainContainer>
    </>
  );
}

// Layout Containers
const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  padding-top: ${({ theme }) => theme.spaces[6]};
  min-height: 100vh;
`;

const ContentContainer = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding-inline: ${({ theme }) => theme.spaces[6]};
`;

const AnimatedContentContainer = styled.div<{ $isVisible: boolean }>`
  ${({ $isVisible }) => css`
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    left: 0;
    transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;

    /* Default hidden styles */
    transform: translateY(100%);
    opacity: 0;
    visibility: hidden;
    position: absolute;
    min-height: 0;
    top: -9999px;

    /* Visible state overrides */
    ${$isVisible &&
    css`
      transform: translateY(0);
      opacity: 1;
      visibility: visible;
      position: relative;
      min-height: auto;
      top: 0;
    `}
  `}
`;

// Header Section
const DispensaryLogoImage = styled.img`
  object-fit: cover;
  width: 120px;
  margin-bottom: ${({ theme }) => theme.spaces[20]};
`;

const HeadingContainer = styled.div`
  ${({ theme: { colors, spaces } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${spaces[4]};
    align-items: center;
    margin-bottom: ${spaces[6]};
    color: ${colors.grey[40]};
  `}
`;

// Content Elements
const ChecklistItem = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};
`;

const CheckIcon = styled(_CheckIcon).attrs(({ theme }) => ({
  color: theme.customized.colors.buttonsLinks,
}))``;

// Login Section
const Logins = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  text-align: center;

  form {
    margin-bottom: ${({ theme }) => theme.spaces[4]};
`;

const LoginText = styled(Typography.Heading).attrs({
  tag: 'h2',
  size: 'small',
})`
  color: ${({ theme }) => theme.colors.grey[25]};
  margin-bottom: ${({ theme }) => theme.spaces[4]};
  font-size: 18px;
`;

// Typography
const BodyText = styled(Typography.Body)`
  font-size: 13px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.grey[45]};
`;

// Interactive Elements
const Button = styled(_Button)`
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};
  text-transform: none;
`;

const SignupLink = styled(Typography.Body)`
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};
`;

// Footer Section
const FooterContainer = styled.footer`
  ${({ theme: { spaces } }) => css`
    height: 84px;
    width: 100%;
    display: flex;
    gap: ${spaces[1]};
    justify-content: center;
    align-items: center;
    margin-top: 25px;
    padding-bottom: ${spaces[6]};
    position: relative;
    z-index: 1;
  `}
`;
